import * as PodcastServices from 'widget/PodcastEpisode/services';
import baseLogger from 'widget/logger';
import Logger from 'modules/Logger/Logger';
import playbackTypes from 'widget/constants/playback';
import qs from 'qs';
import { applyConfigToProps } from 'widget/PodcastEpisode/helpers';
import {
  ConfigurableProps,
  PodcastEpisodeResolvedProps,
} from 'widget/PodcastEpisode/types';
import { getIdFromSlug } from 'state/Routing/helpers';
import { Request } from 'express';
import { TransportInterface } from 'widget/utils/transport/types';
import { WIDGET_DIMENSIONS } from 'constants/widgetDimensions';
import { WidgetLocationConfig } from 'widget/types';

export type InitialProps = {
  logger: Logger;
  params: {
    episode: string;
    id: string;
    namespace: keyof typeof playbackTypes;
  };
  query: Request['query'];
  transport: TransportInterface;
  websiteUrl: string;
};

type InstanceMethods = {
  logger?: Logger;
  transport: TransportInterface;
};

export default async function getInitialProps(
  { params, query }: InitialProps,
  { logger = baseLogger, transport }: InstanceMethods,
  { websiteUrl = '' }: WidgetLocationConfig,
): Promise<PodcastEpisodeResolvedProps> {
  const { episode, id, namespace } = params;
  const { autoplay } = query;
  const episodeId = getIdFromSlug(episode);
  const podcastId = getIdFromSlug(id);

  const fetchPodcast = PodcastServices.fetchPodcast({
    logger,
    podcastId,
    transport,
  });

  const fetchPodcastEpisode = PodcastServices.fetchPodcastEpisode({
    episodeId,
    logger,
    transport,
  });

  const [
    { imageUrl: profileImageUrl, title: subHeading },
    { description, imageUrl, title },
  ] = await Promise.all([fetchPodcast, fetchPodcastEpisode]);

  // query string parameters for analytics so that we can tell the user came to the episode profile / podcast profile from widget
  const trackParamsforLandingUrls = {
    keyid: [subHeading, title],
    sc: 'podcast_widget',
  };

  const tracks = [];
  if (episodeId) {
    tracks.push({ stationId: String(podcastId), trackId: String(episodeId) });
  }

  const basePodcastUrl = `${websiteUrl}/${namespace}/${id}/`;
  const baseEpisodeUrl = `${basePodcastUrl}episode/${episode}/`;

  const configureableProps: ConfigurableProps = applyConfigToProps(
    {
      description,
      imageUrl,
      subHeading,
    },
    query,
  );

  return {
    autoplay: !!autoplay,
    baseEpisodeUrl,
    basePodcastUrl,
    dimensions: { ...WIDGET_DIMENSIONS.PODCAST_EPISODE },
    episodeId: String(episodeId),
    followUrl: `${basePodcastUrl}?${qs.stringify({
      ...trackParamsforLandingUrls,
      follow: true,
    })}`,
    profileImageUrl,
    ogType: 'video',
    origQuery: query,
    podcastEpisodeUrl: `${baseEpisodeUrl}?${qs.stringify(
      trackParamsforLandingUrls,
    )}`,
    podcastId: String(podcastId),
    podcastUrl: `${basePodcastUrl}?${qs.stringify(trackParamsforLandingUrls)}`,
    title: subHeading ? `${title} - ${subHeading}` : title,
    tracks,
    urlWithQuery: `${baseEpisodeUrl}?${qs.stringify(query)}`,
    ...configureableProps,
  };
}
